import React, { useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import humps from 'humps';
import { PartnerContext } from './partners/partner';
import * as User from './user';

export const useCreativeInput = (
  fieldNames,
  creative,
  setCreative,
  CreativeService
) => {
  const onChange = (event, value) => {
    const fieldName = event.target.name;
    // Checkboxes pass the value as a second param, not on the target
    const fieldValue = value === undefined ? event.target.value : value;
    setCreative(prevCreative => ({
      ...prevCreative,
      [fieldName]: fieldValue
    }));
    CreativeService && CreativeService.set(fieldName, fieldValue);
  };

  const creativeInputProps = {};

  // Initialize data inside creative service (and rendered demo)
  // This was breaking group creatives, and is seemingly unneeded, leaving
  /// for future investigation if we need it??
  // React.useEffect(function init() {
  //   fieldNames.forEach(fieldName => {
  //     CreativeService && CreativeService.set(fieldName, creative[fieldName]);
  //   });
  // }, []);

  fieldNames.forEach(fieldName => {
    if (fieldName === 'thumbnailUrl' || fieldName === 'brandLogoUrl' || fieldName === 'srtFileUrl') {
      creativeInputProps[`${fieldName}Props`] = {
        value: creative[fieldName] || '',
        onChange: fieldValue => {
          setCreative({
            ...creative,
            [fieldName]: fieldValue
          });
          CreativeService && CreativeService.set(fieldName, fieldValue);
        }
      };
    } else {
      creativeInputProps[`${fieldName}Props`] = {
        onChange,
        value: creative[fieldName] || '',
        name: fieldName,
        // TODO: entertain the idea of unique-ifying this (because multi-card
        // builder!)
        id: fieldName
      };
    }
  });
  return creativeInputProps;
};

export const useNgCreativeService = (
  creative,
  CreativeService,
  RenderService
) => {
  const debouncedUpdate = React.useCallback(
    creative => {
      CreativeService.setAttrs(humps.camelizeKeys(creative));
      RenderService.renderDemo();
    },
    // never update the debounced callback binding
    []
  );
  React.useEffect(() => debouncedUpdate(creative), [creative]);
};

/* Pass delay = null to pause the interval */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef(() => {});

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (delay === null) {
      return;
    }

    function tick() {
      savedCallback.current && savedCallback.current();
    }

    let id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

export const UserContext = React.createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const isMounted = useIsMounted();

  React.useEffect(function fetchCurrentUser() {
    User.getCurrent().then(currentUser => {
      if (!isMounted()) {
        return;
      }
      setUser(currentUser);
    });
  }, []);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = { children: PropTypes.node.isRequired };

// user will be null if not yet fetched
export const useCurrentUser = () => {
  const user = React.useContext(UserContext);
  if (user === undefined) {
    throw new Error('useCurrentUser must be used from within a UserProvider');
  }
  return user;
};

export const useIsMounted = () => {
  const ref = useRef(true);

  useEffect(() => {
    return () => {
      ref.current = false;
    };
  }, []);

  const isMounted = () => {
    if (ref.current) {
      return true;
    }
    return false;
  };

  return isMounted;
};

export const useCurrentPartner = () => {
  const partner = useContext(PartnerContext);
  if (partner === undefined) {
    throw new Error('useCurrentPartner must be used within a PartnerProvider');
  }
  return partner;
};
